// src/App.jsx
import React, { useState } from "react";
import { HomePage } from "./homePage.jsx";

/* ------------ Simple local login gate ------------ */

const AGENTS = {
  UTC1104: "7015424776",
  UTC1105: "Cred@123",
  // add more here
};

function LoginScreen({ onLoginSuccess }) {
  const [userId, setUserId] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const expected = AGENTS[userId.trim()];
    if (expected && password === expected) {
      setError("");
      onLoginSuccess();
    } else {
      setError("Invalid ID or Password");
    }
  };

  return (
    <div className="app-root">
      <div className="app-shell">
        <header className="app-header">
          <h1 className="app-header-title">Agent Login</h1>
        </header>

        <main className="app-main app-main--login">
          <form
            onSubmit={handleSubmit}
            className="card login-card"
            style={{ maxWidth: 360 }}
          >
            <h3>Welcome to Utc Finance</h3>

            <div className="form-group">
              <label className="field-label" htmlFor="loginUserId">
                User ID
              </label>
              <input
                id="loginUserId"
                type="text"
                className="field-input"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label className="field-label" htmlFor="loginPassword">
                Password
              </label>
              <input
                id="loginPassword"
                type="password"
                className="field-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="off"
              />
            </div>

            {error && <p className="text-error">{error}</p>}

            <button
              type="submit"
              className="btn btn-primary login-btn"
              style={{ marginTop: 12 }}
            >
              Login
            </button>
          </form>
        </main>
      </div>
    </div>
  );
}

/* ------------ Tabs ------------ */

const TABS = {
  HOME: "HOME",
  ELIGIBILITY: "ELIGIBILITY",
  EMI: "EMI",
};

function App() {
  const [activeTab, setActiveTab] = useState(TABS.HOME);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  if (!isLoggedIn) {
    return <LoginScreen onLoginSuccess={() => setIsLoggedIn(true)} />;
  }

  return (
    <div className="app-root">
      <div className="app-shell">
        <header className="app-header">
          <h1 className="app-header-title">Welcome, Agent.</h1>
        </header>

        <main className="app-main">
          {activeTab === TABS.HOME && <HomePage />}
          {activeTab === TABS.ELIGIBILITY && <EligibilityPage />}
          {activeTab === TABS.EMI && <EmiCalculatorPage />}
        </main>

        <BottomNav activeTab={activeTab} onChange={setActiveTab} />
      </div>
    </div>
  );
}

function BottomNav({ activeTab, onChange }) {
  return (
    <nav className="bottom-nav">
      <button
        className={
          "bottom-nav-item" +
          (activeTab === TABS.HOME ? " bottom-nav-item--active" : "")
        }
        onClick={() => onChange(TABS.HOME)}
      >
        Home
      </button>

      <button
        className={
          "bottom-nav-item bottom-nav-item--center" +
          (activeTab === TABS.ELIGIBILITY
            ? " bottom-nav-item--center-active"
            : "")
        }
        onClick={() => onChange(TABS.ELIGIBILITY)}
      >
        <span className="eligibility-icon">✓</span>
      </button>

      <button
        className={
          "bottom-nav-item" +
          (activeTab === TABS.EMI ? " bottom-nav-item--active" : "")
        }
        onClick={() => onChange(TABS.EMI)}
      >
        Check EMI
      </button>
    </nav>
  );
}

/* ------------ EMI helpers ------------ */

const TENURE_OPTIONS = [
  { key: "RAPID", label: "Rapid", months: 1 },
  { key: "E3", label: "E3", months: 3 },
  { key: "E6", label: "E6", months: 6 },
  { key: "E9", label: "E9", months: 9 },
  { key: "E12", label: "E12", months: 12 },
];

// Disbursement-based mapping
function getAllowedTenuresByDisbursement(disbursementAmount) {
  const d = Number(disbursementAmount) || 0;
  if (d <= 0) return [];

  if (d <= 5000) {
    // 0 – 5,000
    return ["RAPID", "E3"];
  } else if (d <= 10000) {
    // 5,001 – 10,000
    return ["RAPID", "E3", "E6"];
  } else if (d <= 30000) {
    // 10,001 – 30,000
    return ["RAPID", "E3", "E6", "E9"];
  } else {
    // 30,001+
    return ["RAPID", "E3", "E6", "E9", "E12"];
  }
}

function getMinimumDownPayment(itemValue) {
  const num = Number(itemValue) || 0;
  if (num <= 0) return 0;

  if (num >= 10000 && num <= 20000) {
    return Math.ceil(num * 0.3);
  } else if (num >= 20001 && num <= 35000) {
    return Math.ceil(num * 0.3);
  } else if (num >= 35001 && num <= 50000) {
    return Math.ceil(num * 0.25);
  } else if (num >= 50001) {
    return Math.ceil(num * 0.2);
  }
  return 0;
}

function diffInDays(startDate, endDate) {
  const msPerDay = 24 * 60 * 60 * 1000;
  const start = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const end = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  );
  // Add 1 to include the current day in the count
  return Math.max(0, Math.round((end - start) / msPerDay) + 1);
}

// main EMI generator
// main EMI generator
export function calculateEmiOptions(
  itemValue,
  downPayment,
  deviceType = "ANDROID",
  isExistingUser = false
) {
  const item = Number(itemValue) || 0;
  const down = Number(downPayment) || 0;

  const disbursementAmount = Math.max(item - down, 0);
  if (disbursementAmount <= 0) return [];

  const AV_FEE = 125;
  const LOCK_FEE = deviceType === "IOS" ? 400 : 150;

  const gross = disbursementAmount + AV_FEE + LOCK_FEE;

  const loanAmount = Math.ceil((gross * 100) / 90);

  const pfRate = isExistingUser ? 0.08 : 0.1;
  const processingFee = Math.round(loanAmount * pfRate);

  const allowedKeys = getAllowedTenuresByDisbursement(disbursementAmount);
  if (allowedKeys.length === 0) return [];
  const today = new Date();

  const filteredTenures = TENURE_OPTIONS.filter((t) =>
    allowedKeys.includes(t.key)
  );

  const dailyRate = 0.00065753424658;

  return filteredTenures.map((t) => {
    const months = t.months;

    // Build EMI schedule on fixed dates (1, 10, 20)
    const schedule = [];
    let emiDate = getNextEmiDate(today); // first EMI date
    for (let i = 0; i < months; i++) {
      schedule.push(new Date(emiDate));
      // next EMI one month later, same 1/10/20 bucket
      emiDate = new Date(
        emiDate.getFullYear(),
        emiDate.getMonth() + 1,
        emiDate.getDate()
      );
    }

    const firstDate = schedule[0];
    const lastDate = schedule[schedule.length - 1];

    const days = diffInDays(today, lastDate); // actual tenure days

    const interest = loanAmount * dailyRate * days;
    const rawTotalRepay = loanAmount + interest;

    // NEW LOGIC: force all EMIs to be exactly equal
    const rawEmi = rawTotalRepay / months;
    const emi = Math.ceil(rawEmi);              // round EMI up
    const totalRepay = emi * months;            // adjust total to match equal EMIs

    const firstEmiDate = firstDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const lastRepaymentDate = lastDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    return {
      key: t.key,
      label: t.label,
      months,
      days,
      loanAmount,
      netDisbursement: disbursementAmount,
      emi,
      processingFee,
      interest,
      totalRepay,
      firstEmiDate,
      lastRepaymentDate,
      avFee: AV_FEE,
      appLockFee: LOCK_FEE,
      totalCharges: processingFee,
      actualProcessingFee: processingFee - AV_FEE - LOCK_FEE,
    };
  });
}

function getNextEmiDate(baseDate) {
  const d = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate()
  );
  const day = d.getDate();

  // 1–10 → 10 next month; 11–20 → 20 next month; 21–31 → 1 month after next
  let targetMonth = d.getMonth() + 1; // at least next month
  let targetDay;

  if (day <= 10) {
    targetDay = 10;
  } else if (day <= 20) {
    targetDay = 20;
  } else {
    targetDay = 1;
    targetMonth += 1;
  }

  return new Date(d.getFullYear(), targetMonth, targetDay);
}

/* ------------ Credit-limit helper ------------ */

export function computeApprovedCreditLimit({
  itemValue,
  downPayment,
  income,
  cibil,
  existingEmi,
}) {
  const item = Number(itemValue) || 0;
  const down = Number(downPayment) || 0;
  const inc = Number(income) || 0;
  const cib = Number(cibil) || 0;
  const currEmi = Number(existingEmi) || 0;

  if (!item || item <= 0) {
    return { ok: false, error: "Enter a valid Item Value." };
  }
  if (!down || down <= 0) {
    return { ok: false, error: "Enter a valid downpayment." };
  }
  if (down > item) {
    return { ok: false, error: "Downpayment cannot be more than Item Value." };
  }
  if (!inc || inc < 10000) {
    return { ok: false, error: "Enter a valid monthly income (min 10,000)." };
  }
  if (cib !== 0 && (cib < 300 || cib > 900)) {
    return { ok: false, error: "Enter a valid CIBIL score (300–900) or 0." };
  }

  const dpPct = (down / item) * 100;
  let requiredMinPct = 0;

  if (item >= 10000 && item <= 20000) {
    requiredMinPct = 30;
  } else if (item >= 20001 && item <= 35000) {
    requiredMinPct = 30;
  } else if (item >= 35001 && item <= 50000) {
    requiredMinPct = 25;
  } else if (item >= 50001) {
    requiredMinPct = 20;
  }

  if (requiredMinPct === 0) {
    return {
      ok: false,
      error: "Item value is outside the allowed range for this product.",
    };
  }

  if (dpPct < requiredMinPct) {
    return {
      ok: false,
      error: `Minimum downpayment for this item value is ${requiredMinPct}% of item value.`,
    };
  }

  // Base disbursement and global cap
  let creditLimit = Math.max(item - down, 0);
  creditLimit = Math.min(creditLimit, 35000);

  // CIBIL caps
  let cibilCap = 0;

  if (cib === 0) {
    cibilCap = 15000;
  } else if (cib >= 750) {
    cibilCap = 35000;
  } else if (cib >= 725) {
    cibilCap = 33000;
  } else if (cib >= 700) {
    cibilCap = 30000;
  } else if (cib >= 675) {
    cibilCap = 26000;
  } else if (cib >= 650) {
    cibilCap = 21000;
  } else {
    cibilCap = 0;
  }

  if (cibilCap === 0) {
    return {
      ok: false,
      error: "Customer not eligible as per CIBIL band.",
    };
  }

  creditLimit = Math.min(creditLimit, cibilCap);

  // Special rule: CIBIL 0 => flat 15,000 limit
  if (cib === 0) {
    creditLimit = 15000;
  }

  // FOIR
  const foirCurrent = inc > 0 ? (currEmi / inc) * 100 : 0;

  let maxFoir = 0;
  if (inc >= 10000 && inc <= 20000) {
    maxFoir = 50;
  } else if (inc >= 20001 && inc <= 30000) {
    maxFoir = 55;
  } else if (inc >= 30001 && inc <= 50000) {
    maxFoir = 60;
  } else if (inc >= 50001 && inc <= 75000) {
    maxFoir = 70;
  } else if (inc > 75000) {
    maxFoir = 75;
  }

  if (maxFoir === 0) {
    return { ok: false, error: "No FOIR rule found for this income band." };
  }

  if (foirCurrent > maxFoir) {
    return {
      ok: false,
      error: `FOIR exceeds the maximum allowed ${maxFoir}% for this income band.`,
    };
  }

  if (creditLimit <= 0) {
    return {
      ok: false,
      error: "No credit limit available for this combination.",
    };
  }

  return { ok: true, creditLimit };
}

/* ------------ Eligibility page ------------ */

function EligibilityPage() {
  const [eligibilityEmiOptions, setEligibilityEmiOptions] =
    React.useState([]);
  const [itemValue, setItemValue] = React.useState("");
  const [downPayment, setDownPayment] = React.useState("");
  const [currentIncome, setCurrentIncome] = React.useState("");
  const [cibilScore, setCibilScore] = React.useState("");
  const [currentEmi, setCurrentEmi] = React.useState("");
  const [panCard, setPanCard] = React.useState("");
  const [error, setError] = React.useState("");
  const [result, setResult] = React.useState(null);
  const [showPopup, setShowPopup] = React.useState(false);
  const [popupMessage, setPopupMessage] = React.useState("");
  const [deviceType, setDeviceType] = React.useState("ANDROID");
  const [isExistingUser, setIsExistingUser] = React.useState(false);
  const [extraLimit, setExtraLimit] = React.useState(0);
  const [dpdLast2Months, setDpdLast2Months] = React.useState("");
  const [totalEnquiries, setTotalEnquiries] = React.useState("");

  const handleReset = () => {
    setItemValue("");
    setDownPayment("");
    setCurrentIncome("");
    setCibilScore("");
    setCurrentEmi("");
    setPanCard("");
    setError("");
    setDpdLast2Months("");
    setTotalEnquiries("");
    setResult(null);
    setEligibilityEmiOptions([]);
    setIsExistingUser(false);
    setExtraLimit(0);
  };

  // PAN → check existing user
  async function checkExistingUser(pan) {
    const cleanPan = pan.trim().toUpperCase();
    if (cleanPan.length !== 10) {
      setIsExistingUser(false);
      setExtraLimit(0);
      return;
    }

    try {
      const res = await fetch(
        `https://usethecred.com/api/check_customer.php?pan=${cleanPan}`
      );
      const json = await res.json();

      if (!res.ok || !json.success) {
        setIsExistingUser(false);
        setExtraLimit(0);
        return;
      }

      const loansTaken = Number(json.loans_taken || 0);
      setIsExistingUser(json.existing === true);
      setExtraLimit(loansTaken * 3000);
    } catch (e) {
      console.error("PAN lookup failed", e);
      setIsExistingUser(false);
      setExtraLimit(0);
    }
  }

  const handleCheckLimit = async () => {
    setError("");
    setResult(null);
    setEligibilityEmiOptions([]);
  
    const autoDown = getMinimumDownPayment(itemValue);
    if (!autoDown) {
      setError("Enter a valid item value within the eligible range.");
      return;
    }
  
    const dpd = Number(dpdLast2Months) || 0;
    const enquiries = Number(totalEnquiries) || 0;
  
    if (enquiries > 6) {
      setError("Customer is ineligible: too many enquiries this month (>6).");
      return;
    }
  
    if (dpd > 30) {
      setError("Customer is ineligible due to DPD > 30 days in last 2 months.");
      return;
    }
  
    if (!panCard || panCard.trim().length !== 10) {
      setError("Enter a valid PAN.");
      return;
    }
  
    // 1) Fresh PAN lookup
    const cleanPan = panCard.trim().toUpperCase();
    let existingFlag = false;
    let extraLimitLocal = 0;
  
    try {
      const res = await fetch(
        `https://usethecred.com/api/check_customer.php?pan=${cleanPan}`
      );
      const json = await res.json();
  
      if (res.ok && json.success) {
        const loansTaken = Number(json.loans_taken || 0);
        existingFlag = json.existing === true;
        extraLimitLocal = loansTaken * 3000;
        setIsExistingUser(existingFlag);
        setExtraLimit(extraLimitLocal);
      } else {
        setIsExistingUser(false);
        setExtraLimit(0);
      }
    } catch (e) {
      console.error("PAN lookup failed", e);
      setIsExistingUser(false);
      setExtraLimit(0);
    }
  
    // 2) Base eligibility using minimum DP
    const eligibility = computeApprovedCreditLimit({
      itemValue,
      downPayment: autoDown,
      income: currentIncome,
      cibil: cibilScore,
      existingEmi: currentEmi,
    });
  
    if (!eligibility.ok) {
      setError(eligibility.error);
      return;
    }
  
    // 3) Start from base credit limit, then apply bump + DPD
    let creditLimit = eligibility.creditLimit;   // base disbursal cap
  
    if (existingFlag && extraLimitLocal > 0) {
      creditLimit += extraLimitLocal;           // allow > 35,000
    }
  
    if (dpd > 0 && dpd <= 30) {
      creditLimit = Math.floor(creditLimit * 0.7);
    }
  
    if (creditLimit <= 0) {
      setError("Customer is ineligible: effective credit limit is 0.");
      return;
    }
  
    // 4) Final disbursal equals final creditLimit
    const disbursal = creditLimit;
  
    const item = Number(itemValue) || 0;
    // If no extra downpayment entered by user, DP = item - disbursal
    const effectiveDownPayment = Math.max(item - disbursal, 0);
  
    // 5) Generate EMI options using item price + effective DP
    let emiOptions = calculateEmiOptions(
      item,
      effectiveDownPayment,
      deviceType,
      existingFlag
    );
    console.log("DEBUG_LIMIT", { creditLimit, disbursal, effectiveDownPayment, emiOptions });

  
    // Ensure the card's netDisbursement matches final creditLimit
    emiOptions = emiOptions.map((opt) => ({
      ...opt,
      netDisbursement: disbursal,
    }));
  
    if (!emiOptions || emiOptions.length === 0) {
      setError("No EMI options available for this credit limit.");
      return;
    }
  
    setEligibilityEmiOptions(emiOptions);
    setDownPayment(String(effectiveDownPayment));
  
    const dpdNote =
      dpd > 0 && dpd <= 29 ? " (reduced to 50% due to DPD 0–29 days)" : "";
  
    setPopupMessage(
      `Congratulations! The customer is eligible for a credit limit of ₹${disbursal.toLocaleString(
        "en-IN",
        { maximumFractionDigits: 0 }
      )} with a minimum downpayment of ₹${effectiveDownPayment.toLocaleString(
        "en-IN",
        { maximumFractionDigits: 0 }
      )}${dpdNote}.`
    );
    setShowPopup(true);
  };
  
  

  const handleShowEmiFromPopup = () => {
    setShowPopup(false);
  };

  return (
    <div className="page page-eligibility">
      <h2 className="page-title">Eligibility</h2>

      <div className="form-group">
        <label className="field-label">Item Value</label>
        <input
          type="number"
          className="field-input"
          placeholder="Enter item value"
          value={itemValue}
          onChange={(e) => setItemValue(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Current Income (monthly)</label>
        <input
          type="number"
          className="field-input"
          placeholder="Enter monthly income"
          value={currentIncome}
          onChange={(e) => setCurrentIncome(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="field-label">CIBIL Score</label>
        <input
          type="number"
          className="field-input"
          placeholder="Enter CIBIL score"
          value={cibilScore}
          onChange={(e) => setCibilScore(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Current EMI (total)</label>
        <input
          type="number"
          className="field-input"
          placeholder="Enter total existing EMIs"
          value={currentEmi}
          onChange={(e) => setCurrentEmi(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="field-label">PAN Card</label>
        <input
          type="text"
          className="field-input"
          placeholder="ABCDE1234F"
          value={panCard}
          onChange={(e) => {
            const value = e.target.value.toUpperCase();
            setPanCard(value);
            checkExistingUser(value);
          }}
        />
      </div>

      <div className="form-group">
        <label className="field-label">DPD (last 2 months)</label>
        <input
          type="number"
          className="field-input"
          placeholder="Enter DPD days (0–90)"
          value={dpdLast2Months}
          onChange={(e) => setDpdLast2Months(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Total Enquiries (current month)</label>
        <input
          type="number"
          className="field-input"
          placeholder="Number of bureau enquiries"
          value={totalEnquiries}
          onChange={(e) => setTotalEnquiries(e.target.value)}
        />
      </div>

      <div className="button-row" style={{ marginTop: 12 }}>
        <button
          type="button"
          className={
            "btn device-toggle-btn" +
            (deviceType === "ANDROID" ? " device-toggle-btn--active" : "")
          }
          onClick={() => setDeviceType("ANDROID")}
        >
          Android
        </button>
        <button
          type="button"
          className={
            "btn device-toggle-btn" +
            (deviceType === "IOS" ? " device-toggle-btn--active" : "")
          }
          onClick={() => setDeviceType("IOS")}
        >
          iOS
        </button>
      </div>

      {error && <p className="text-error">{error}</p>}

      <div className="button-row">
        <button className="btn btn-primary" onClick={handleCheckLimit}>
          Check Limit
        </button>
        <button className="btn btn-secondary" onClick={handleReset}>
          Reset
        </button>
      </div>

      {result && (
        <p className="text-note" style={{ marginTop: 12 }}>
          {result}
        </p>
      )}

      {eligibilityEmiOptions.length > 0 && (
        <div className="card-list card-list--emi" style={{ marginTop: 16 }}>
          {eligibilityEmiOptions.map((opt) => (
            <div key={opt.key} className="card emi-card">
              <div className="card-header-row">
                <div className="emi-title">
                  {opt.label} • {opt.months} month
                  {opt.months > 1 ? "s" : ""}
                </div>
              </div>

              <div className="emi-row">
                <span className="emi-label">Downpayment</span>
                <span className="emi-value">
                  ₹{Number(downPayment || 0).toFixed(0)}
                </span>
              </div>

              <div className="emi-row">
                <span className="emi-label">Net Disbursement</span>
                <span className="emi-value">
                  ₹{opt.netDisbursement.toFixed(0)}
                </span>
              </div>

              <div className="emi-row emi-row--highlight">
                <span className="emi-label">Monthly EMI</span>
                <span className="emi-value emi-value--primary">
                  ₹{opt.emi.toFixed(0)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {eligibilityEmiOptions.length > 0 && (
        <div className="loan-amount-pill">
          XXXX-
          {eligibilityEmiOptions[0].loanAmount.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}
        </div>
      )}

      {showPopup && (
        <div className="sheet-backdrop" onClick={() => setShowPopup(false)}>
          <div
            className="card"
            style={{
              maxWidth: 360,
              margin: "0 auto",
              padding: 20,
              background: "#111827",
              color: "#f9fafb",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="page-title" style={{ marginBottom: 8 }}>
              Congratulations!
            </h3>
            <p className="text-note" style={{ marginBottom: 16 }}>
              {popupMessage}
            </p>
            <button
              className="btn btn-primary"
              style={{ width: "100%" }}
              onClick={handleShowEmiFromPopup}
            >
              Show EMI options
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------ Check EMI page ------------ */

function EmiCalculatorPage() {
  const [itemValue, setItemValue] = React.useState("");
  const [downPayment, setDownPayment] = React.useState("");
  const [minDownPayment, setMinDownPayment] = React.useState(0);
  const [options, setOptions] = React.useState([]);
  const [error, setError] = React.useState("");
  const [hasGenerated, setHasGenerated] = React.useState(false);
  const [deviceType, setDeviceType] = React.useState("ANDROID");
  const [isExistingUser, setIsExistingUser] = React.useState(false);

  const handleItemChange = (e) => {
    const val = e.target.value;
    setItemValue(val);

    const num = Number(val) || 0;
    let minDp = 0;

    if (num > 0) {
      if (num >= 10000 && num <= 20000) {
        minDp = Math.ceil(num * 0.3);
      } else if (num >= 20001 && num <= 35000) {
        minDp = Math.ceil(num * 0.3);
      } else if (num >= 35001 && num <= 50000) {
        minDp = Math.ceil(num * 0.25);
      } else if (num >= 50001) {
        minDp = Math.ceil(num * 0.2);
      }
    }

    setMinDownPayment(minDp);
    setDownPayment("");
    setOptions([]);
    setHasGenerated(false);
    setError("");
  };

  const handleDownChange = (e) => {
    setDownPayment(e.target.value);
  };

  const handleGenerate = () => {
    setError("");
    const item = Number(itemValue) || 0;
    const down = Number(downPayment) || 0;

    if (!item || item <= 0) {
      setError("Enter a valid item value.");
      setOptions([]);
      setHasGenerated(false);
      return;
    }

    if (!down || down <= 0) {
      setError("Enter a downpayment.");
      setOptions([]);
      setHasGenerated(false);
      return;
    }

    if (minDownPayment > 0 && down < minDownPayment) {
      setError(
        `Downpayment must be at least ₹${minDownPayment.toLocaleString(
          "en-IN",
          { maximumFractionDigits: 0 }
        )}.`
      );
      setOptions([]);
      setHasGenerated(false);
      return;
    }

    if (down > item) {
      setError("Downpayment cannot be more than item value.");
      setOptions([]);
      setHasGenerated(false);
      return;
    }

    const result = calculateEmiOptions(
      item,
      down,
      deviceType,
      isExistingUser
    );
    if (result.length === 0) {
      setError("Loan amount must be greater than 0.");
      setOptions([]);
      setHasGenerated(false);
      return;
    }

    setOptions(result);
    setHasGenerated(true);
  };

  const handleReset = () => {
    setItemValue("");
    setDownPayment("");
    setMinDownPayment(0);
    setOptions([]);
    setError("");
    setHasGenerated(false);
    setIsExistingUser(false);
  };

  return (
    <div className="page page-emi">
      <h2 className="page-title">Check EMI</h2>

      <div className="form-group">
        <label className="field-label">Item Value</label>
        <input
          type="number"
          className="field-input"
          placeholder="Enter item value"
          value={itemValue}
          onChange={handleItemChange}
        />
        {minDownPayment > 0 && (
          <p
            className="text-note"
            style={{ color: "green", fontSize: 12, marginTop: 4 }}
          >
            Minimum downpayment: ₹
            {minDownPayment.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </p>
        )}
      </div>

      <div className="form-group">
        <label className="field-label">Downpayment</label>
        <input
          type="number"
          className="field-input"
          placeholder="Enter downpayment"
          value={downPayment}
          onChange={handleDownChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label">Existing Customer?</label>
        <div className="button-row" style={{ marginTop: 4 }}>
          <button
            type="button"
            className={
              "btn device-toggle-btn" +
              (!isExistingUser ? " device-toggle-btn--active" : "")
            }
            onClick={() => setIsExistingUser(false)}
          >
            New
          </button>
          <button
            type="button"
            className={
              "btn device-toggle-btn" +
              (isExistingUser ? " device-toggle-btn--active" : "")
            }
            onClick={() => setIsExistingUser(true)}
          >
            Existing
          </button>
        </div>
      </div>

      {error && <p className="text-error">{error}</p>}

      <div className="button-row" style={{ marginTop: 12 }}>
        <button className="btn btn-primary" onClick={handleGenerate}>
          Generate
        </button>
        <button className="btn btn-secondary" onClick={handleReset}>
          Reset
        </button>
      </div>

      <p className="text-note">
        * These EMIs are indicative and calculated as of{" "}
        {new Date().toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })}
        .
      </p>

      {hasGenerated && options.length > 0 && (
        <div className="card-list card-list--emi">
          {options.map((opt) => (
            <div key={opt.key} className="card emi-card">
              <div className="card-header-row">
                <div className="emi-title">
                  {opt.label} • {opt.months} month
                  {opt.months > 1 ? "s" : ""}
                </div>
              </div>

              <div className="emi-row">
                <span className="emi-label">EMI Date</span>
                <span className="emi-value">{opt.firstEmiDate || "-"}</span>
              </div>

              <div className="emi-row">
                <span className="emi-label">Disbursal Amount</span>
                <span className="emi-value">
                  ₹{opt.netDisbursement.toFixed(0)}
                </span>
              </div>

              <div className="emi-row emi-row--highlight">
                <span className="emi-label">Monthly EMI</span>
                <span className="emi-value emi-value--primary">
                  ₹{opt.emi.toFixed(0)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasGenerated && options.length > 0 && (
        <div className="loan-amount-pill">
          ₹
          {options[0].loanAmount.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}
        </div>
      )}
    </div>
  );
}

export default App;
